<template>
  <div class="max-container">
    <h1 class="text-4xl">
      {{ getPageTitle() }}
    </h1>

    <!-- Top search area -->
    <div class="mt-1 mb-6 flex gap-2">
      <label class="w-full">
        <span class="sr-only">Search</span>
        <input
          v-model="inputText"
          type="text"
          class="form-control px-4 py-2 text-lg grow"
          @keydown.enter="search"
        >
      </label>
      <button
        type="button"
        class="btn btn-gray btn-primary"
        @click="search"
      >
        Search
      </button>
    </div>

    <!-- Loading skeleton -->
    <template v-if="state.loading">
      <div class="flex gap-6">
        <div class="hidden md:block">
          <SdsLoadingSkeleton
            height="h-96"
            width="w-72"
          />
        </div>
        <div class="flex flex-col w-full gap-6">
          <SdsLoadingSkeleton
            v-for="i in 3"
            :key="i"
            height="h-28"
            width="w-full"
          />
        </div>
      </div>
    </template>

    <template v-else-if="state.results.length < 1 && state.hasRunSearch">
      <p class="text-gray-700 text-base">
        Your search retrieved no results. Search again.
      </p>
    </template>

    <template v-else-if="state.error">
      <div class="text-gray-700 text-base">
        Oops! Something went wrong. Please modify your search and try again.
      </div>
    </template>

    <!-- Filters and results -->
    <div
      v-else
      class="flex flex-col md:flex-row gap-6 w-full"
    >
      <!-- Filters sidebar -->
      <div
        v-if="state.info?.facets"
        class="relative border border-gray-200 md:border-0 md:p-0 shrink-0 md:w-72"
      >
        <div id="filters">
          <!-- Mobile filter button -->
          <button
            class="md:hidden w-full p-4 text-red-600 hover:bg-gray-25 text-left font-semibold"
            type="button"
            @click="showMobileFilters = !showMobileFilters"
          >
            Filter
          </button>
          <!-- Filter groups -->
          <div
            v-if="state.info?.facets"
            :class="{
              'hidden': !showMobileFilters,
            }"
            class="md:block p-4 md:p-0 space-y-4"
          >
            <!-- Filter list item -->
            <search-facet-group
              v-for="key in nonEmptyFacetKeys"
              :key="key"
              :facet-name="key"
              :facet-obj="state.info?.facets[key]"
              :selected="state.selectedFacets[key] || []"
              :expanded="state.expandedFacets[key] ?? false"
              @update:selected="updateSelectedFacets(key, $event)"
              @update:expanded="updateExpandedFacets(key)"
            />
          </div>
        </div>
      </div>

      <!-- Search results -->
      <div class="grow">
        <div>
          <!-- Showing/Results per page section -->
          <div
            v-if="state.info?.total_result_count > 0"
            class="flex flex-col sm:flex-row w-full gap-4 items-center mb-4"
          >
            <div class="grow">
              <p class="grow text-gray-500 text-base">
                {{ showingResultsText }}
              </p>
            </div>
            <div class="flex items-center gap-2">
              <label
                class="flex items-center gap-2 text-gray-700 text-sm whitespace-nowrap"
              >
                <span>Results per page</span>
                <select
                  v-model="state.perPage"
                  class="border border-gray-100 hover:border-gray-200"
                  aria-label="Results per page"
                  @change="setResultsPerPage"
                >
                  <option
                    v-for="item in [10, 25, 50]"
                    :key="item"
                    :value="item"
                  >
                    {{ item }}
                  </option>
                </select>
              </label>
            </div>
          </div>

          <!-- Selected filters -->
          <div
            v-if="state.info?.total_result_count > 0 && Object.keys(state.selectedFacets).length > 0"
            class="mb-6 flex flex-wrap gap-2 items-center"
          >
            <template v-for="(values, facet) in state.selectedFacets">
              <search-badge
                v-for="value in values"
                :key="facet + '-' + value"
                :facet="facet"
                :value="value"
                @remove="clearSingleFilter(facet, value)"
              />
            </template>
            <button
              class="p-1 text-red-600 text-sm font-medium hover:underline transition-colors"
              type="button"
              @click="clearAllFilters"
            >
              clear all
            </button>
          </div>

          <!-- Search result item -->
          <search-result
            v-for="result in state.results"
            :key="JSON.stringify(result)"
            :result="result"
            :query="state.query"
          />

          <!-- Paginator -->
          <search-paginator
            v-if="state.info?.num_pages > 1"
            :current-page="state.page"
            :num-pages="state.info?.num_pages || 1"
            @go-to-page="gotoPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { ofetch } from 'ofetch'

// --- Wagtail Boilerplate ---

interface SearchPage {
  uuid: string,
  type?: 'main' | 'blog' | 'digitalLibrary' | null
}

// Define props with type and default values using withDefaults
const props = withDefaults(defineProps<SearchPage>(), {
  uuid: '',
  type: 'main'
});

// --- Typescript Types/Interfaces ---

interface ISearchInfo {
  current_page?: number
  facets?: {
    AssetTypeName?: Record<string, number>
    sei_topic?: Record<string, number>
    sei_year_published?: Record<string, number>
    sei_author?: Record<string, number>
  }
  num_pages?: number
  per_page?: number
  query?: string
  total_result_count?: number
}

export interface ISearchResult {
  title?: string
  Description?: string
  image?: string
  url?: string
  type?: string
  sei_date_published?: string
  [key: string]: unknown
}

const rootElement = ref(null);

onBeforeMount(() => {
  rootElement.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement);
  delete rootElement.value.dataset['json']
});

// --- URL Query Helpers ---

function fullyDecodeURIComponent(str: string): string {
  let prev;
  let decoded = str;
  do {
    prev = decoded;
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      break;
    }
  } while (prev !== decoded);
  // eslint-disable-next-line
  return decoded;
}

const getQueryParams = () => typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search)

const getPageTitle = () => {
  if (props.type === 'blog') return 'Blog Search Results'
  if (props.type === 'digitalLibrary') return 'Digital Library Search Results'
  return 'Search Results'
}

const parseFacetsFromParams = (params: URLSearchParams): Record<string, string[]> => {
  const facets: Record<string, string[]> = {}
  params.forEach((value, key) => {
    if (key.startsWith('facet_')) {
      const facetName = key.slice(6)
      if (!facets[facetName]) facets[facetName] = []
      facets[facetName].push(fullyDecodeURIComponent(value))
    }
  })
  return facets
}

const updateUrlQuery = ({ query, page, perPage, selectedFacets }: { query: string, page: number, perPage: number, selectedFacets: Record<string, string[]> }) => {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams()
  if (query) params.set('q', fullyDecodeURIComponent(query))
  if (page > 1) params.set('page', String(page))
  if (perPage !== 10) params.set('per_page', String(perPage))
  for (const [facet, values] of Object.entries(selectedFacets)) {
    if (props.type === 'blog' && facet === 'AssetTypeName') continue
    for (const value of values) {
      params.append(`facet_${facet}`, fullyDecodeURIComponent(value))
    }
  }
  const url = new URL(window.location.href)
  url.search = params.toString()
  window.history.replaceState({}, '', url.toString())
}

// --- Scroll To Top Helper ---

const scrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// --- State Initialization from URL ---

const params = getQueryParams()
const initialQuery = fullyDecodeURIComponent(params.get('q') || '').replace(/\s+/g, ' ').trim();
const initialPage = parseInt(params.get('page') || '1', 10)
const initialPerPage = parseInt(params.get('per_page') || '10', 10)
const initialFacets = parseFacetsFromParams(params)

// --- Reactive References ---

const showMobileFilters = ref(false)

const inputText = ref('')

const state = reactive({
  query: initialQuery,
  info: null as ISearchInfo,
  results: [] as ISearchResult[],
  loading: false,
  hasRunSearch: false,
  error: null as string | null,
  perPage: initialPerPage,
  page: initialPage,
  selectedFacets: initialFacets as Record<string, string[]>,
  expandedFacets: {} as Record<string, boolean>,
})

// --- Computed Properties ---

const facet_order = { AssetTypeName: 1, sei_topic: 2, sei_year_published: 3, sei_author: 4 };

const nonEmptyFacetKeys = computed(() =>
  Object.keys(state.info?.facets || {})
    .filter(key => Object.keys(state.info?.facets[key] || {}).length > 0)
    .sort((a, b) => (facet_order[a] || 99) - (facet_order[b] || 99))
)

const showingResultsText = computed(() => {
  if (!state.results.length) return 'Your search retrieved no results. Search again.'
  const info = state.info
  const page = info?.current_page || 1
  const perPage = info?.per_page || state.results.length || 10
  const total = info?.total_result_count || 0
  const start = ((page - 1) * perPage) + 1
  const end = Math.min(page * perPage, total)
  return `Results ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()}`
})

// --- Watchers ---

// Sync input text with query state
watch(() => state.query, (newQuery) => {
  inputText.value = newQuery
})

// --- API call ---

const search = () => {
  state.query = inputText.value
  state.results = []
  state.info = null
  state.selectedFacets = {}
  state.page = 1
  callApi()
}

const clearSingleFilter = (facet, value) => {
  state.page = 1
  const newFacets = { ...state.selectedFacets }
  const arr = Array.isArray(newFacets[facet]) ? [...newFacets[facet]] : []
  const idx = arr.indexOf(value)
  if (idx !== -1) arr.splice(idx, 1)
  if (arr.length === 0) {
    delete newFacets[facet]
  } else {
    newFacets[facet] = arr
  }
  state.selectedFacets = newFacets
  callApi()
}

const clearAllFilters = () => {
  state.page = 1
  state.selectedFacets = {}
  callApi()
}

const updateSelectedFacets = (facetName: string, value: string) => {
  showMobileFilters.value = false
  const newFacets = { ...state.selectedFacets }
  const arr = Array.isArray(newFacets[facetName]) ? [...newFacets[facetName]] : []
  const idx = arr.indexOf(value)
  if (idx !== -1) {
    arr.splice(idx, 1)
  } else {
    arr.push(value)
  }
  if (arr.length === 0) {
    delete newFacets[facetName]
  } else {
    newFacets[facetName] = arr
  }
  state.selectedFacets = newFacets
  state.page = 1
  scrollToTop()
  callApi()
}

const updateExpandedFacets = (facetName: string) => {
  state.expandedFacets[facetName] = !state.expandedFacets[facetName]
}

const setResultsPerPage = () => {
  state.page = 1
  callApi()
}

const gotoPage = (page: number) => {
  if (!state.info) return
  const numPages = state.info.num_pages || 1
  if (page < 1 || page > numPages || page === (state.info.current_page || 1)) return
  state.page = page
  callApi()
}

const callApi = async () => {
  const { query: q, page, perPage, selectedFacets } = state
  // Build filter params for selected facets
  const facetFilters = Object.fromEntries(
    Object.entries(selectedFacets).map(([facet, values]) => [`filters[page][${facet}][]`, values])
  )
  state.loading = true
  state.hasRunSearch = true
  state.error = null
  try {
    const data = await ofetch('https://api.swiftype.com/api/v1/public/engines/search.json', {
      method: 'GET',
      params: {
        q: encodeURI(fullyDecodeURIComponent(q)).replaceAll(/%2B/g, '+').replaceAll(/%20/g, '+'),
        engine_key: 'oteHz_7i3Rcyi-inH43h',
        page,
        per_page: perPage,
        'functional_boosts[page][sei_site_priority]': 'linear',
        // If this is a blog search we will have only blog results.
        // Do not show asset type facet because it is not relevant for
        // blog content.
        'facets[page][]': [
          'sei_year_published',
          'sei_topic',
          ...(props.type !== 'blog' ? ['AssetTypeName'] : []),
          'sei_author',
        ],
        'filters[page][type]': (
          props.type === 'blog' ?
            // Only show blog results
            ['blog'] :
          props.type === 'digitalLibrary' ?
            // don't show blog results for digital library searches
            ['!page', '!event-private', '!website', '!blog'] :
            // for main searches, include all result types
          props.type === 'main' ?
            [] :
            // default to all result types
            []
        ),
        'filters[document_type][name]': 'page',
        ...facetFilters,
        'filters[page][q]': q,
        spelling: 'strict',
        'highlight_fields[page][Description][fallback]': true,
        'highlight_fields[page][title][fallback]': true,
        search_timeout: 5000,
        _: Date.now()
      },
    })
    state.info = data.info?.page ? { ...data.info.page } : null
    state.results = data.records?.page || []
    state.error = null
  } catch (err) {
    state.error = err.message
    state.info = null
    state.results = []
  } finally {
    state.loading = false
    scrollToTop()
    // Keep URL in sync with state
    updateUrlQuery({
      query: state.query,
      page: state.page,
      perPage: state.perPage,
      selectedFacets: state.selectedFacets
    })
    // lint-disable-next-line
    // console.log(props.type)
  }
}

// --- Lifecycle Hooks ---

onMounted(() => {
  inputText.value = state.query
  callApi()
})
</script>
